/**
 * LotSmith — Per-account detail page.
 *
 * Sets the active account on mount so LiveProvider's existing effect
 * refetches metrics / equity / trades for the route param. Displays the
 * firm-specific rules, the same metric grid as the overview page, the
 * scoped equity curve, and recent trades.
 */
import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";

import { useDev } from "../providers/dataContext.js";
import { MetricCard } from "../components/MetricCard.js";
import { EquityCurve } from "../components/EquityCurve.js";
import { RecentTrades } from "../components/RecentTrades.js";
import { EaStateBanner } from "../components/EaStateBanner.js";
import { DisciplineInsights } from "../components/DisciplineInsights.js";
import { EmptyState, ErrorState, Skeleton } from "../components/States.js";

const FIRM_LABELS: Record<string, string> = {
  FTMO: "FTMO",
  FUNDED_NEXT: "FundedNext",
  MFF: "MyForexFunds",
  FIVE_PERCENTERS: "The 5%ers",
  TOPSTEP: "TopStep",
  E8: "E8",
  FUNDING_PIPS: "Funding Pips",
  PERSONAL: "Personal account",
  OTHER: "Other",
};

export function AccountDetail() {
  const { id } = useParams();
  const {
    accounts, activeAccountId, setActiveAccountId,
    isLoading, error, metrics, equity, trades, reload,
  } = useDev();

  // Sync URL param → active account so LiveProvider refetches.
  useEffect(() => {
    if (id && id !== activeAccountId) setActiveAccountId(id);
  }, [id, activeAccountId, setActiveAccountId]);

  const account = accounts.find((a) => a.id === id);

  if (error) {
    return (
      <div className="dash">
        <Link to="/app" className="dash__back">&larr; Overview</Link>
        <ErrorState title="Couldn't load account" body={error} cta={{ label: "Try again", onClick: reload }} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="dash">
        <Link to="/app" className="dash__back">&larr; Overview</Link>
        <div style={{ padding: 16 }}><Skeleton height="20px" lines={4} /></div>
      </div>
    );
  }

  // Account list loaded but the URL id doesn't resolve — could be a stale
  // bookmark or a revoked account. Surface a clean dead-end with a way back.
  if (!account) {
    return (
      <div className="dash">
        <Link to="/app" className="dash__back">&larr; Overview</Link>
        <EmptyState
          title="Account not found"
          body="This account may have been removed, or the link is no longer valid."
          cta={{ label: "Back to overview", onClick: () => { window.location.href = "/app"; } }}
        />
      </div>
    );
  }

  const firmLabel = FIRM_LABELS[account.propFirm] ?? account.propFirm;

  return (
    <div className="dash">
      <Link to="/app" className="dash__back">&larr; Overview</Link>
      <div className="dash__hdr">
        <div>
          <div className="dash__eyebrow">
            {firmLabel} · {account.phase} · #{account.brokerLogin}
          </div>
          <h1 className="dash__title">{firmLabel}</h1>
        </div>
      </div>

      {account.eaState && (
        <EaStateBanner
          state={account.eaState}
          reason={account.eaStateReason}
          since={account.eaStateUpdatedAt}
        />
      )}

      <section className="panel">
        <div className="panel__hdr">
          <h2 className="panel__title">Rules</h2>
        </div>
        {metrics?.rules ? (
          <div className="rules-grid">
            <RuleRow label="Prop firm"     value={firmLabel} />
            <RuleRow label="Daily loss"    value={`${(metrics.rules.dailyLossPct * 100).toFixed(1)} % of $${account.startBalance.toLocaleString("en-US")}`} />
            <RuleRow label="Total loss"    value={`${(metrics.rules.totalLossPct * 100).toFixed(1)} %`} />
            <RuleRow label="High water"    value={`$${metrics.highWaterEquity.toLocaleString("en-US", { maximumFractionDigits: 2 })} · ${Math.round(metrics.trailingDrawdownUsedPct * 100)} % trailing DD used`} />
            <RuleRow label="Profit target" value={`${(metrics.rules.targetPct * 100).toFixed(1)} %`} />
            <RuleRow label="Reset tz"      value={metrics.rules.resetTimezone} />
            <RuleRow label="News policy"   value={formatNewsPolicy(metrics.rules)} />
          </div>
        ) : (
          <div className="panel__body panel__body--muted">
            Rules unavailable — waiting on the metrics endpoint.
          </div>
        )}
      </section>

      {metrics ? (
        <div className="metric-row">
          <MetricCard
            label="Equity"
            value={metrics.equity}
            format="usd"
            delta={`${formatSigned(metrics.equityDelta)} · ${formatPct(metrics.equityDeltaPct)}`}
            deltaTone={metrics.equityDelta >= 0 ? "profit" : "loss"}
          />
          <MetricCard
            label="Daily P&L"
            value={metrics.dailyPnl}
            format="usd-signed"
            valueTone={metrics.dailyPnl >= 0 ? "profit" : "loss"}
            delta={`${metrics.tradesToday} trades · ${Math.round(metrics.winRate * 100)}% win`}
          />
          <MetricCard
            label="Daily loss left"
            value={metrics.dailyLossLeft}
            format="usd"
            delta={`${Math.round(metrics.dailyLossUsedPct * 100)}% used`}
            deltaTone={metrics.dailyLossUsedPct > 0.7 ? "warning" : "muted"}
          />
          <MetricCard
            label="To target"
            value={metrics.toTarget}
            format="usd"
            delta={`${Math.round(metrics.targetProgressPct * 100)}%`}
            deltaTone="muted"
          />
          <MetricCard
            label="Discipline alerts"
            value={metrics.revengeTradesToday}
            format="raw"
            valueTone={metrics.revengeTradesToday > 0 ? "warning" : "profit"}
            delta={`revenge-risk · ${metrics.revengeWindowMinutes}m`}
            deltaTone={metrics.revengeTradesToday > 0 ? "warning" : "muted"}
          />
        </div>
      ) : null}

      {equity.length > 0 && (
        <section className="panel">
          <div className="panel__hdr">
            <h2 className="panel__title">Equity curve</h2>
          </div>
          <EquityCurve points={equity} />
        </section>
      )}

      {metrics && <DisciplineInsights insights={metrics.disciplineInsights} />}

      {trades.length > 0 ? (
        <section className="panel">
          <div className="panel__hdr">
            <h2 className="panel__title">Recent trades</h2>
          </div>
          <RecentTrades trades={trades} />
        </section>
      ) : (
        <EmptyState
          title="No trades on this account"
          body="Once you place your first trade with the EA, it'll appear here."
        />
      )}
    </div>
  );
}

interface RuleRowProps { label: string; value: string }
function RuleRow({ label, value }: RuleRowProps) {
  return (
    <div className="rules-grid__row">
      <div className="rules-grid__label">{label}</div>
      <div className="rules-grid__value">{value}</div>
    </div>
  );
}

function formatSigned(n: number): string {
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatPct(n: number): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(2)}%`;
}

interface NewsRules {
  newsPolicy?: "allowed" | "discouraged" | "blackout";
  newsBlackoutMinutes?: number;
  newsNotes?: string;
}
function formatNewsPolicy(r: NewsRules): string {
  if (!r.newsPolicy) return "—";
  if (r.newsPolicy === "blackout") {
    return `Blackout ±${r.newsBlackoutMinutes ?? 0} min · ${r.newsNotes ?? ""}`;
  }
  if (r.newsPolicy === "discouraged") {
    return `Discouraged · ${r.newsNotes ?? ""}`;
  }
  return `Allowed · ${r.newsNotes ?? ""}`;
}
